import { useCallback } from "react"
import { BaseError, TransactionReceipt } from "viem"
import {
  Config,
  useAccount,
  useChainId,
  useConfig,
  usePublicClient,
  UseWriteContractReturnType,
  useWriteContract as useWriteContractWagmi,
} from "wagmi"
import { waitForTransactionReceipt } from "wagmi/actions"
import { hardhat } from "wagmi/chains"
import { toast } from "sonner"

// this type is needed as the hook can't infer its own type 🫠
// https://github.com/microsoft/TypeScript/issues/42873#issuecomment-2065572017
type WriteContractAsyncAbx<
  config extends Config = Config,
  context = unknown,
> = ReturnType<typeof useWriteContractWagmi> & {
  writeContractAsyncNative: UseWriteContractReturnType<
    config,
    context
  >["writeContractAsync"]
  writeContractAsync: (
    opts: Parameters<
      UseWriteContractReturnType<config, context>["writeContractAsync"]
    >[0] & {
      loading?: string
      success?: string
      error?: string
      onTxHash?: (txHash: string) => void
    },
  ) => TransactionReceipt
  // ) => UseWriteContractReturnType<config, context>["writeContractAsync"]
}

const mappings = {
  OwnableUnauthorizedAccount: "You must be the owner",
}
const humanizeError = (error: any) => {
  const parsedError = /Error: (\w+)/.exec(error.message)
  if (!parsedError) {
    return
  }
  return mappings[parsedError[1] as any] || parsedError[1]
}

/**
 * This wraps the useWriteContract hook from wagmi
 * to add default toast and waiting etc.
 * You might want to add errors in the humanizeError function
 */
export const useWriteContract = (key_?: string) => {
  const { address } = useAccount()
  const { writeContractAsync: writeContractNative, ...rest } =
    useWriteContractWagmi()
  const chainId = useChainId()
  const config = useConfig()
  const publicClient = usePublicClient({ chainId })

  const writeContractAsync = useCallback(
    async (
      {
        loading = "Confirm transaction",
        success = "Transaction success!",
        error = "Failed to send transaction",
        onTxHash,
        ...wagmiNative
      }: {
        loading?: string
        success?: string
        error?: string
        onTxHash?: (txHash: string) => void
      } & Parameters<typeof writeContractNative>[0],
      hooks = undefined,
    ) => {
      const key = key_ || JSON.stringify(wagmiNative)
      try {
        console.log({ writeArgs: wagmiNative })

        if (process.env.NODE_ENV === "development" && chainId === hardhat.id) {
          const nonce = await publicClient!.getTransactionCount({
            address: address!,
          })
          toast.info(`Expected nonce: ${nonce}`, {
            position: "bottom-left",
          })
        }

        toast.loading("Awaiting confirmation...", { id: key })
        const hash = await writeContractNative(wagmiNative, hooks)
        toast.loading(loading, { id: key })
        await onTxHash?.(hash)

        const receipt = await waitForTransactionReceipt(config, {
          hash,
        })

        toast.success(success, { id: key, duration: 2000 })

        return receipt
      } catch (e: any) {
        try {
          console.log(
            // @ts-ignore
            (e as BaseError).walk().message.split(":")[1].split("Version")[0],
          )
        } catch {
          /* just want to make sure we don't throw on logging */
        }

        const errorMessage = humanizeError(e)
        console.log(e.message)
        if (
          /User rejected the request/.test(e.message) ||
          /User denied transaction signature/.test(e.message)
        ) {
          toast.warning("Cancelled", { id: key, duration: 2000 })
        } else {
          console.log("Error", e)
          toast.error(error || "Error sending transaction", {
            id: key,
            duration: 2000,
          })
        }
        throw errorMessage || e
      }
    },
    [chainId, writeContractNative, config, publicClient, address, key_],
  )

  return {
    ...rest,
    writeContractAsyncNative: writeContractNative,
    writeContractAsync,
  } as unknown as WriteContractAsyncAbx
}
